import { useEffect, useRef } from "react";
import type { AnimationAction, AnimationMixer } from "three";

import { useEngineStore } from "@/store/useEngineStore";
import type { AnimationName } from "@/types/engine.types";
import { DEFAULT_ENGINE_CONFIG } from "@/types/engine.types";

interface BlendTreeParams {
  actions: Record<string, AnimationAction | null>;
  mixer: AnimationMixer;
  agentId: string;
}

export function useBlendTree({ actions, mixer, agentId }: BlendTreeParams) {
  const currentRef = useRef<AnimationName>("Idle_A");

  const agent = useEngineStore((s) => s.agents[agentId]);
  const selectedAgentId = useEngineStore((s) => s.selectedAgentId);
  const selectCount = useEngineStore((s) => s.selectCount);

  const speed = agent?.speed ?? 0;
  const status = agent?.status ?? "idle";

  // Play Idle_A on mount
  useEffect(() => {
    const idle = actions["Idle_A"];
    if (idle) {
      idle.reset().fadeIn(0.3).play();
    }
  }, [actions]);

  // Locomotion transitions
  useEffect(() => {
    const current = actions[currentRef.current];
    if (!current) return;

    if (status === "moving" && speed > 0) {
      const targetName: AnimationName =
        speed >= DEFAULT_ENGINE_CONFIG.agentRunSpeedThreshold ? "Run" : "Walk";
      const target = actions[targetName];
      if (target && currentRef.current !== targetName) {
        target.reset().fadeIn(0.3).play();
        current.fadeOut(0.3);
        currentRef.current = targetName;
      }
    } else if (status === "idle" && speed === 0) {
      if (currentRef.current === "Walk" || currentRef.current === "Run") {
        const idle = actions["Idle_A"];
        if (idle) {
          idle.reset().fadeIn(0.4).play();
          current.fadeOut(0.4);
          currentRef.current = "Idle_A";
        }
      }
    }
  }, [actions, speed, status]);

  // Selection click animation — triggers on every click, not just first selection
  useEffect(() => {
    if (selectedAgentId !== agentId || selectCount === 0) return;

    const clicked = actions["Clicked"];
    const current = actions[currentRef.current];
    if (clicked && current) {
      const prevAnim = currentRef.current;
      clicked.reset().setLoop(2200, 1).clampWhenFinished = true;
      clicked.fadeIn(0.15).play();
      current.fadeOut(0.15);

      const onFinished = () => {
        const restore = actions[prevAnim];
        if (restore) {
          restore.reset().fadeIn(0.2).play();
          clicked.fadeOut(0.2);
          currentRef.current = prevAnim;
        }
        mixer.removeEventListener("finished", onFinished);
      };
      mixer.addEventListener("finished", onFinished);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectCount]);

  return { currentAnimation: currentRef.current };
}
